import React, { useState } from 'react';

import { Alert, Badge, Spinner, Tabs, Tab } from 'react-bootstrap';
import dayjs from 'dayjs';
import { Link, useParams } from 'react-router-dom';

import Container from 'components/shared/Container';
import { CUT_DRY_IMPORT_RUN_DETAILS, getURL } from 'lib/networking/endpoints';
import CutDryImportTrackingOverviewTab from 'components/cut_dry_product_import_tracking/CutDryImportTrackingOverviewTab';
import CutDryImportTrackingProductsTab from 'components/cut_dry_product_import_tracking/CutDryImportTrackingProductsTab';
import useGet from 'lib/hooks/useGet';

const CUT_DRY_IMPORT_TRACKING_TABS = {
  OVERVIEW: 'overview',
  NEW_PRODUCTS: 'new_products',
};

const CutDryImportTrackingDetails = () => {
  const { runId } = useParams();
  const [activeTab, setActiveTab] = useState(CUT_DRY_IMPORT_TRACKING_TABS.OVERVIEW);

  // Fetch import run details
  const {
    data: { import_run: importRun = null } = {},
    loading: importRunLoading,
    error: importRunError,
  } = useGet(getURL(CUT_DRY_IMPORT_RUN_DETAILS, { run_id: runId }));

  const getStatusBadge = status => {
    const statusColors = {
      COMPLETED: 'success',
      FAILED: 'danger',
      IN_PROGRESS: 'warning',
      PENDING: 'secondary',
    };
    return <Badge variant={statusColors[status] || 'secondary'}>{status}</Badge>;
  };

  const getActionBadge = action => {
    const actionColors = {
      CREATED: 'success',
      UPDATED: 'info',
      SKIPPED: 'secondary',
    };
    return <Badge variant={actionColors[action] || 'secondary'}>{action}</Badge>;
  };

  const formatDuration = (startTime, endTime) => {
    if (!endTime) return 'In Progress';
    const start = dayjs(startTime);
    const end = dayjs(endTime);
    const duration = end.diff(start, 'minute', true);
    return `${duration.toFixed(1)} minutes`;
  };

  if (importRunLoading) {
    return (
      <Container fluid>
        <div className="text-center mt-5">
          <Spinner animation="border" />
        </div>
      </Container>
    );
  }

  if (importRunError) {
    return (
      <Container fluid>
        <Alert variant="danger">Error loading import run: {importRunError.message}</Alert>
      </Container>
    );
  }

  if (!importRun) {
    return (
      <Container fluid>
        <Alert variant="danger">Import run not found</Alert>
      </Container>
    );
  }

  return (
    <Container fluid>
      <div className="page-header">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/cut-dry-import-tracking">Cut Dry Import Tracking</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Run #{importRun.id}
            </li>
          </ol>
        </nav>
        <h3 className="font-weight-bold">Cut Dry Import Run #{importRun.id}</h3>
      </div>

      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
        <Tab eventKey={CUT_DRY_IMPORT_TRACKING_TABS.OVERVIEW} title="Overview">
          <CutDryImportTrackingOverviewTab
            importRun={importRun}
            getStatusBadge={getStatusBadge}
            formatDuration={formatDuration}
          />
        </Tab>

        {importRun.new_products_created > 0 && (
          <Tab eventKey={CUT_DRY_IMPORT_TRACKING_TABS.NEW_PRODUCTS} title="New Products">
            <CutDryImportTrackingProductsTab
              runId={runId}
              importRun={importRun}
              getActionBadge={getActionBadge}
            />
          </Tab>
        )}
      </Tabs>
    </Container>
  );
};

export default CutDryImportTrackingDetails;
